<template>
  <div class="employee-course-dashboard">
    <div class="mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">Courses</h1>
      <p class="text-gray-500 dark:text-gray-400">Complete your training courses and earn certificates.</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div v-for="i in 6" :key="i" class="card p-0 overflow-hidden animate-pulse">
        <div class="h-40 bg-gray-200 dark:bg-gray-600"></div>
        <div class="p-5 space-y-3">
          <div class="h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
          <div class="h-2 bg-gray-200 dark:bg-gray-600 rounded-full w-full"></div>
          <div class="h-9 bg-gray-200 dark:bg-gray-600 rounded-lg w-28"></div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
      <div class="flex items-center gap-2"><AlertCircle class="w-5 h-5 text-red-500" /><span class="text-red-700 dark:text-red-300">{{ error }}</span></div>
      <button @click="fetchCourses" class="mt-2 text-sm text-red-600 underline">Try again</button>
    </div>

    <!-- Course Cards Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div
        v-for="course in courses"
        :key="course.course_id"
        class="card p-0 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group"
      >
        <!-- Cover Image -->
        <div class="h-60 bg-gradient-to-br from-primary/20 to-primary/5 dark:from-primary/30 dark:to-primary/10 flex items-center justify-center relative overflow-hidden">
          <img
            v-if="course.cover_image"
            :src="course.cover_image"
            :alt="course.name"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <GraduationCap v-else class="w-16 h-16 text-primary/40" />
          <!-- Status badge -->
          <span
            class="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold shadow"
            :class="statusBadgeClass(course)"
          >
            {{ isCourseCompleted(course) ? 'Completed' : course.status_name }}
          </span>
        </div>

        <div class="p-5">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate">{{ course.name }}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">{{ course.description || 'No description' }}</p>

          <!-- Progress bar -->
          <div class="mb-2">
            <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>{{ course.completed_lessons }}/{{ course.total_lessons }} lessons</span>
              <span>{{ Math.round(course.overall_progress) }}%</span>
            </div>
            <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-500"
                :class="course.overall_progress >= 100 ? 'bg-green-500' : 'bg-primary'"
                :style="{ width: course.overall_progress + '%' }"
              ></div>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="flex items-center gap-2 mt-4">
            <template v-if="isCourseCompleted(course)">
              <button
                class="btn btn-primary btn-sm flex items-center gap-1.5"
                @click="startCourse(course)"
              >
                <Eye class="w-4 h-4" /> View
              </button>
              <!-- TEMPORARILY COMMENTED: Certificate button
              <button
                v-if="course.certificate_available"
                class="btn btn-success btn-sm flex items-center gap-1.5"
                @click="viewCertificate(course)"
              >
                <Award class="w-4 h-4" /> Certificate
              </button>
              -->
            </template>
            <button
              v-else
              class="btn btn-primary btn-sm flex items-center gap-1.5"
              @click="startCourse(course)"
            >
              <PlayCircle class="w-4 h-4" />
              {{ course.status === 1 && course.overall_progress > 0 ? 'Resume' : 'Start' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !error && courses.length === 0" class="text-center py-12">
      <GraduationCap class="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
      <h3 class="text-lg font-semibold text-gray-600 dark:text-gray-400">No courses available</h3>
      <p class="text-sm text-gray-400 mt-1">Check back later for new training courses.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { dashboardApi } from '@/services/dashboard'
import { useTitle } from '@/composables/ui/useTitle'
import type { EmployeeCourse } from '@/services/courseService'
import { GraduationCap, PlayCircle, Award, AlertCircle, Eye } from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
useTitle(`Courses - ${appTitle.value}`)

const router = useRouter()
const courses = ref<EmployeeCourse[]>([])
const loading = ref(true)
const error = ref('')

function isCourseCompleted(course: EmployeeCourse): boolean {
  return course.overall_progress >= 100
}

function statusBadgeClass(course: EmployeeCourse) {
  if (isCourseCompleted(course)) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
  switch (course.status) {
    case 0: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    case 1: return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    case 2: return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
    default: return ''
  }
}

function startCourse(course: EmployeeCourse) {
  router.push({ name: 'course-content', params: { courseId: course.course_id } })
}

// TEMPORARILY COMMENTED: viewCertificate
// function viewCertificate(course: EmployeeCourse) {
//   router.push({ name: 'my-certificates' })// }

async function fetchCourses() {
  loading.value = true
  error.value = ''
  try {
    courses.value = await dashboardApi.getEmployeeCourses()
  } catch (e: any) {
    error.value = e?.message || 'Failed to load courses'
  } finally {
    loading.value = false
  }
}

onMounted(fetchCourses)
</script>
